import { gql, useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { Server } from '../../apollo/types';
import {
  AppointmentType,
  GetUpcomingAppointmentsQuery,
  GetUpcomingAppointmentsQueryVariables,
} from '../schedule-preview/types';
import { inSessionPatientInformationAtom } from '../state';

export const GET_IN_SESSION_APPOINTMENT = gql`
  query GetInSessionAppointment($filters: AppointmentsFilter!) {
    allAppointments(filters: $filters) {
      edges {
        node {
          appointmentShortId
          appointmentType
          timeEnd
          timeStart
        }
      }
    }
  }
`;

type InSessionAppointment = {
  appointmentShortId: string;
  appointmentType: AppointmentType;
  timeEnd: string;
  timeStart: string;
};

export function useAppointmentByShortId(appointmentShortId?: string): InSessionAppointment | undefined {
  const { data } = useQuery<GetUpcomingAppointmentsQuery, GetUpcomingAppointmentsQueryVariables>(
    GET_IN_SESSION_APPOINTMENT,
    {
      skip: !appointmentShortId,
      variables: {
        filters: {
          shortIds: [appointmentShortId!],
          canceled: false,
        },
      },
      context: { server: Server.MONOLITH },
    },
  );

  return useMemo(() => {
    const appointment = data?.allAppointments?.edges?.[0]?.node;

    if (!appointment?.timeEnd || !appointment.timeStart) {
      return undefined;
    }

    return {
      appointmentShortId: appointment.appointmentShortId,
      appointmentType: appointment.appointmentType,
      timeEnd: appointment.timeEnd,
      timeStart: appointment.timeStart,
    };
  }, [data?.allAppointments?.edges]);
}

export function useInSessionAppointment(): InSessionAppointment | undefined {
  const inSessionPatientInfo = useAtomValue(inSessionPatientInformationAtom);
  return useAppointmentByShortId(inSessionPatientInfo?.inSessionAppointmentShortId);
}
